import React, { useState, useEffect, useMemo } from "react";
import { menuList } from "./list";
import MenuCard from "./MenuCard";
import "./NewSideBar.scss";
import SidebarItemCollapse from "./SideBarItemCollapse";
import SidebarItem from "./SideBarItem";
import SvgLogo from "../../assets/icons/SvgLogo";
import SvgFinalLogo from "../../assets/icons/SvgFinalLogo";
import { useLocation, useNavigate } from "react-router-dom";
import findNamesByPath from "../../utility/findSidBarNames";
import Cookies from "js-cookie";
import { InputText } from "primereact/inputtext";

const NewSideBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pathArrayData, setPathArrayData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const currentPathname = location?.pathname;

  // Get user role from cookies
  const userRole = Cookies.get("USER_ROLE");

  // Filter menu based on user role
  const baseFilteredMenuList = menuList.filter((menu) => {
    // If user is not admin, hide the Master menu
    if (userRole === "user" && menu.name === "Master") {
      return false;
    }
    return true;
  });

  // Create a flattened list of all menu items for searching
  const flattenMenuItems = useMemo(() => {
    const items = [];

    const addItem = (item, parent = null) => {
      items.push({
        ...item,
        parent: parent,
        label: parent ? `${parent} > ${item.name}` : item.name,
        searchableText: item.name.toLowerCase()
      });

      if (item.submenu) {
        item.submenu.forEach(subItem => addItem(subItem, item.name));
      }
    };

    baseFilteredMenuList.forEach(item => addItem(item));
    return items;
  }, [baseFilteredMenuList]);

  // Filter menu based on search query
  const filteredMenuList = useMemo(() => {
    if (!searchQuery.trim()) {
      return baseFilteredMenuList;
    }

    const query = searchQuery.toLowerCase();
    const matchingItems = new Set();
    const matchingParents = new Set();

    // Find all matching items and their parents
    baseFilteredMenuList.forEach(menu => {
      const menuMatches = menu.name.toLowerCase().includes(query);

      if (menuMatches) {
        matchingItems.add(menu.name);
      }

      if (menu.submenu) {
        menu.submenu.forEach(subItem => {
          if (subItem.name.toLowerCase().includes(query)) {
            matchingItems.add(subItem.name);
            matchingParents.add(menu.name);
          }
        });
      }
    });

    // Filter menu to show only matching items and their parents
    return baseFilteredMenuList.filter(menu => {
      if (matchingItems.has(menu.name) || matchingParents.has(menu.name)) {
        // If parent matches or has matching children, show with filtered submenu
        if (menu.submenu) {
          const filteredSubmenu = menu.submenu.filter(subItem =>
            matchingItems.has(subItem.name) || menu.name.toLowerCase().includes(query)
          );

          if (filteredSubmenu.length > 0 || matchingItems.has(menu.name)) {
            return {
              ...menu,
              submenu: filteredSubmenu.length > 0 ? filteredSubmenu : menu.submenu
            };
          }
        }
        return true;
      }
      return false;
    });
  }, [searchQuery, baseFilteredMenuList]);

  // Handle search and create search results
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = flattenMenuItems
        .filter(item => item.searchableText.includes(query))
        .slice(0, 10); // Limit to 10 results
      setSearchResults(results);
      setSelectedSearchIndex(-1);
    } else {
      setSearchResults([]);
      setSelectedSearchIndex(-1);
    }
  }, [searchQuery, flattenMenuItems]);

  // Handle keyboard navigation in search
  const handleSearchKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSearchIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSearchIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSearchIndex >= 0 && searchResults[selectedSearchIndex]) {
          const item = searchResults[selectedSearchIndex];
          if (item.path) {
            navigate(item.path);
            setSearchQuery("");
            setSearchResults([]);
          }
        }
        break;
      case 'Escape':
        setSearchQuery("");
        setSearchResults([]);
        break;
    }
  };

  // Handle clicking on search result
  const handleSearchResultClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const pathArrayData = findNamesByPath(baseFilteredMenuList, location?.pathname);
    console.log(pathArrayData, "find pathArrayData");
    setPathArrayData(pathArrayData);
  }, [currentPathname, baseFilteredMenuList]);

  return (

    <div className="sidebar__overall__container">
      <ul className="list">
        <div className="stack">
          <div>
            <SvgFinalLogo />
          </div>
        </div>

        {/* Search Box */}
        <div className="menu-search-container">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search menu..."
              className="menu-search-input"
              autoComplete="off"
            />
          </span>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="search-results-dropdown">
              {searchResults.map((item, index) => (
                <div
                  key={index}
                  className={`search-result-item ${selectedSearchIndex === index ? 'selected' : ''}`}
                  onClick={() => handleSearchResultClick(item)}
                  onMouseEnter={() => setSelectedSearchIndex(index)}
                >
                  <i className={item.icon || 'pi pi-angle-right'} />
                  <span className="search-result-text">{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {Array.isArray(pathArrayData) && pathArrayData?.length > 1 && (
          <>
            {filteredMenuList.map((individalMenu, index) =>
              individalMenu.submenu ? (
                <SidebarItemCollapse
                  item={individalMenu}
                  key={index}
                  currentPathname={currentPathname}
                  pathArrayData={pathArrayData}
                />
              ) : (
                <SidebarItem
                  item={individalMenu}
                  key={index}
                  currentPathname={currentPathname}
                  pathArrayData={pathArrayData}
                />
              )
            )}
          </>
        )}
      </ul>
    </div>
  );
};

export default NewSideBar;
